import React, { useEffect, useState } from 'react';
import { getSites, updateSite, deleteSite, getReport, getBrandStats, getPortfolioStats, triggerBenchmark, triggerPortfolioBenchmark, CATEGORIES } from '../api';

const STAGES = [
  { key: 'prospect',    label: 'Prospects',   color: 'var(--muted)' },
  { key: 'assessed',    label: 'Assessed',    color: 'var(--amber)' },
  { key: 'shortlisted', label: 'Shortlisted', color: 'var(--blue)' },
  { key: 'approved',    label: 'Approved',    color: 'var(--green)' },
  { key: 'rejected',    label: 'Rejected',    color: 'var(--red)' },
];

const STAGE_ACTIONS = {
  prospect:    [{ to: 'shortlisted', label: 'Shortlist' }, { to: 'rejected', label: 'Reject' }],
  assessed:    [{ to: 'shortlisted', label: 'Shortlist' }, { to: 'rejected', label: 'Reject' }],
  shortlisted: [{ to: 'approved', label: 'Approve' }, { to: 'assessed', label: 'Back to Assessed' }],
  approved:    [{ to: 'shortlisted', label: 'Back to Shortlisted' }],
  rejected:    [{ to: 'prospect', label: 'Reconsider' }],
};

const _scoreColor = (s) => {
  if (s == null) return 'var(--s-w50)';
  if (s >= 80) return '#1a7a4a';
  if (s >= 65) return '#b07820';
  return '#b03020';
};

export default function PipelinePanel({ onViewReport, onRunReport, onManagePortfolio, isMobile }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [brand, setBrand] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (portfolio && portfolio.count > 0) {
        await triggerPortfolioBenchmark();
      } else {
        await triggerBenchmark();
      }
      setTimeout(async () => {
        const [bs, ps] = await Promise.all([
          getBrandStats().catch(() => null),
          getPortfolioStats().catch(() => null),
        ]);
        setBrand(bs);
        setPortfolio(ps);
        setRefreshing(false);
      }, 10000);
    } catch {
      setRefreshing(false);
    }
  };

  const load = () => {
    getSites(filter === 'all' ? null : filter)
      .then(setSites)
      .catch(() => setSites([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    getBrandStats().then(setBrand).catch(() => setBrand(null));
    getPortfolioStats().then(setPortfolio).catch(() => setPortfolio(null));
  }, []);

  const handleStageChange = async (siteId, newStage) => {
    await updateSite(siteId, { stage: newStage });
    load();
  };

  const handleSaveNotes = async (siteId) => {
    await updateSite(siteId, { notes: editNotes });
    setEditingId(null);
    load();
  };

  const handleDelete = async (siteId) => {
    await deleteSite(siteId);
    load();
  };

  const handleViewReport = async (reportId) => {
    try {
      const full = await getReport(reportId);
      if (full?.report_data) {
        onViewReport(full.report_data, {
          category: full.category, radius_km: full.radius_km,
          format: full.format, anchor: full.anchor, own_brand: full.own_brand,
        }, { lat: full.lat, lon: full.lon });
      }
    } catch (err) {
      console.error('Failed to load report:', err);
    }
  };

  const handleDownloadReport = async (reportId) => {
    try {
      const full = await getReport(reportId);
      if (full?.report_data) {
        onViewReport(full.report_data, {
          category: full.category, radius_km: full.radius_km,
          format: full.format, anchor: full.anchor, own_brand: full.own_brand,
        }, { lat: full.lat, lon: full.lon });
        setTimeout(() => window.print(), 500);
      }
    } catch (err) {
      console.error('Failed to download report:', err);
    }
  };

  const categoryLabel = (key) => {
    if (key === 'shopping_centre') return 'SC Feasibility';
    const cat = CATEGORIES.find(c => c.key === key);
    return cat ? cat.label : key || '—';
  };

  const scoreColor = (s) => {
    if (s == null) return 'var(--muted)';
    if (s >= 80) return 'var(--green)';
    if (s >= 65) return 'var(--amber)';
    return 'var(--red)';
  };

  const stageInfo = (key) => STAGES.find(s => s.key === key) || STAGES[0];

  const stageCounts = {};
  sites.forEach(s => { stageCounts[s.stage] = (stageCounts[s.stage] || 0) + 1; });

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.title}>Site Pipeline</div>
        <div style={styles.statsRow}>
          {STAGES.map(s => (
            <div key={s.key} style={styles.statChip}>
              <div style={{ ...styles.statDot, background: s.color }} />
              <span>{stageCounts[s.key] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...styles.twoCol, ...(isMobile ? { flexDirection: 'column' } : {}) }}>
      <div style={styles.leftCol}>

      {/* Stage filter */}
      <div style={styles.filterRow}>
        <button
          onClick={() => setFilter('all')}
          style={{ ...styles.filterBtn, ...(filter === 'all' ? styles.filterActive : {}) }}
        >
          All ({sites.length})
        </button>
        {STAGES.map(s => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            style={{ ...styles.filterBtn, ...(filter === s.key ? styles.filterActive : {}) }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={styles.empty}>Loading...</div>
      ) : sites.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyTitle}>
            {filter === 'all' ? 'No sites in pipeline' : `No ${filter} sites`}
          </div>
          <div style={styles.emptyText}>
            Sites are automatically saved when you drop a pin on the map.
          </div>
        </div>
      ) : (
        <div style={styles.list}>
          {sites.map(site => {
            const si = stageInfo(site.stage);
            const actions = STAGE_ACTIONS[site.stage] || [];
            const isEditing = editingId === site.id;

            return (
              <div key={site.id} style={styles.card}>
                {/* Top row: score + info + stage */}
                <div style={styles.cardTop}>
                  <div style={styles.scoreBox}>
                    {site.total_score != null ? (
                      <>
                        <div style={{ color: scoreColor(site.total_score), fontFamily: "'News Cycle',serif", fontSize: 22, fontWeight: 700, lineHeight: 1 }}>
                          {site.total_score}
                        </div>
                        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: 'var(--muted)' }}>/100</div>
                      </>
                    ) : (
                      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: 'var(--muted)' }}>—</div>
                    )}
                  </div>

                  <div style={styles.cardInfo}>
                    <div style={styles.cardName}>{site.name || `${site.lat.toFixed(4)}, ${site.lon.toFixed(4)}`}</div>
                    <div style={styles.cardMeta}>
                      {categoryLabel(site.category)} · {site.radius_km}km
                    </div>
                    {site.tags?.length > 0 && (
                      <div style={styles.tagRow}>
                        {site.tags.map(t => <span key={t} style={styles.siteTag}>{t}</span>)}
                      </div>
                    )}
                  </div>

                  <div style={{ ...styles.stageBadge, background: si.color + '18', color: si.color }}>
                    {si.label}
                  </div>
                </div>

                {/* Notes */}
                {isEditing ? (
                  <div style={styles.notesEdit}>
                    <textarea
                      value={editNotes}
                      onChange={e => setEditNotes(e.target.value)}
                      placeholder="Add notes about this site..."
                      style={styles.notesInput}
                      rows={3}
                    />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => handleSaveNotes(site.id)} style={styles.notesSaveBtn}>Save</button>
                      <button onClick={() => setEditingId(null)} style={styles.notesCancelBtn}>Cancel</button>
                    </div>
                  </div>
                ) : site.notes ? (
                  <div style={styles.notesDisplay} onClick={() => { setEditingId(site.id); setEditNotes(site.notes || ''); }}>
                    {site.notes}
                  </div>
                ) : null}

                {/* Actions */}
                <div style={styles.cardActions}>
                  <div style={styles.actionsLeft}>
                    {site.latest_report_id && (
                      <>
                        <button onClick={() => handleViewReport(site.latest_report_id)} style={styles.actionBtn}>
                          View Report
                        </button>
                        <button onClick={() => handleDownloadReport(site.latest_report_id)} style={styles.actionBtn}>
                          Download PDF
                        </button>
                      </>
                    )}
                    {!site.latest_report_id && onRunReport && (
                      <button onClick={() => onRunReport(site)} style={styles.actionBtnPrimary}>
                        Run Report
                      </button>
                    )}
                    <button
                      onClick={() => { setEditingId(site.id); setEditNotes(site.notes || ''); }}
                      style={styles.actionBtn}
                    >
                      Notes
                    </button>
                  </div>
                  <div style={styles.actionsRight}>
                    {actions.map(a => (
                      <button
                        key={a.to}
                        onClick={() => handleStageChange(site.id, a.to)}
                        style={styles.stageBtn}
                      >
                        {a.label}
                      </button>
                    ))}
                    <button onClick={() => handleDelete(site.id)} style={styles.deleteBtn}>
                      Remove
                    </button>
                  </div>
                </div>

                <div style={styles.cardDate}>
                  {new Date(site.created_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>

      {/* Right column — Benchmark */}
      <div style={styles.rightCol}>
        <div style={styles.benchHeader}>
          <div style={styles.benchHeaderTitle}>
            {portfolio && portfolio.count > 0 ? 'Portfolio Benchmark' : 'Network Benchmark'}
          </div>
          <button onClick={handleRefresh} disabled={refreshing} style={styles.benchRefreshBtn}>
            {refreshing ? 'Running...' : 'Refresh Data'}
          </button>
        </div>

        {/* Portfolio Benchmark (property fund) */}
        {portfolio && portfolio.count > 0 ? (
          <>
            <div style={styles.benchSectionLabel}>Portfolio ({portfolio.count} centres)</div>
            {portfolio.scored > 0 ? (
              <div style={styles.benchGrid}>
                <BenchCard value={portfolio.scored} label="Scored" />
                <BenchCard value={portfolio.avg_score || '—'} label="Avg Score" color={_scoreColor(portfolio.avg_score)} />
                <BenchCard value={portfolio.best_score || '—'} label="Best" color={_scoreColor(portfolio.best_score)} />
                <BenchCard value={portfolio.worst_score || '—'} label="Weakest" color={_scoreColor(portfolio.worst_score)} />
              </div>
            ) : (
              <div style={styles.benchEmpty}>
                Portfolio saved but not yet scored. Click "Refresh Data" to benchmark your properties.
              </div>
            )}
            <button onClick={() => onManagePortfolio && onManagePortfolio()} style={styles.benchManageBtn}>
              Manage Portfolio
            </button>
          </>

        /* Brand Benchmark (retail client) */
        ) : brand?.brand ? (
          <>
            <div style={styles.benchBrandName}>{brand.brand}</div>
            {brand.total_stores > 0 ? (
              <>
                {brand.stores_scored > 0 && (
                  <>
                    <div style={styles.benchSectionLabel}>Scoring</div>
                    <div style={styles.benchGrid}>
                      <BenchCard value={brand.stores_scored} label="Scored" />
                      <BenchCard value={brand.avg_score || '—'} label="Avg Score" color={_scoreColor(brand.avg_score)} />
                      <BenchCard value={brand.best_score || '—'} label="Best" color={_scoreColor(brand.best_score)} />
                      <BenchCard value={brand.worst_score || '—'} label="Weakest" color={_scoreColor(brand.worst_score)} />
                    </div>
                  </>
                )}
                <div style={styles.benchSectionLabel}>Network</div>
                <div style={styles.benchGrid}>
                  <BenchCard value={(brand.total_stores || 0).toLocaleString()} label="Total Stores" />
                  <BenchCard value={(brand.operational || 0).toLocaleString()} label="Operational" color="#1a7a4a" />
                  <BenchCard value={`+${brand.new_last_90d || 0}`} label="New (90d)" color="var(--s-text)" />
                  <BenchCard value={brand.avg_rating ? `★ ${brand.avg_rating}` : '—'} label="Avg Rating" />
                </div>
                {brand.latest_scrape && (
                  <div style={styles.benchUpdated}>Last updated: {brand.latest_scrape}</div>
                )}
              </>
            ) : (
              <div style={styles.benchEmpty}>
                No benchmark data yet. Click "Refresh Data" to score your brand's network.
              </div>
            )}
          </>

        ) : (
          <div style={styles.benchEmpty}>
            Set your brand in your profile or add properties to your portfolio to enable benchmarking.
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function BenchCard({ value, label, color }) {
  return (
    <div style={bStyles.card}>
      <div style={{ ...bStyles.value, ...(color ? { color } : {}) }}>{value}</div>
      <div style={bStyles.label}>{label}</div>
    </div>
  );
}

const bStyles = {
  card: { background: 'var(--white)', border: '1px solid var(--rule)', borderRadius: 6, padding: '10px 8px', textAlign: 'center' },
  value: { fontFamily: "'News Cycle',serif", fontSize: 20, fontWeight: 700, color: 'var(--ink)', lineHeight: 1 },
  label: { fontFamily: "'DM Mono',monospace", fontSize: 8, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 4 },
};

const styles = {
  container: { width: '100%', height: '100%', overflowY: 'auto', background: 'var(--cream)', padding: '24px' },
  twoCol: { display: 'flex', alignItems: 'stretch', height: '100%' },
  leftCol: { flex: 2, minWidth: 0, overflowY: 'auto', paddingRight: 20 },
  rightCol: { flex: 1, minWidth: 240, maxWidth: 320, borderLeft: '2px solid var(--rule)', paddingLeft: 20 },
  benchHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid var(--ink)' },
  benchHeaderTitle: { fontFamily: "'News Cycle',serif", fontSize: 18, fontWeight: 700, color: 'var(--ink)' },
  benchRefreshBtn: { padding: '6px 12px', background: 'var(--accent)', color: '#14110e', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: "'Work Sans',sans-serif", letterSpacing: '0.3px' },
  benchBrandName: { fontFamily: "'News Cycle',serif", fontSize: 24, fontWeight: 700, color: 'var(--ink)', marginBottom: 16 },
  benchSectionLabel: { fontFamily: "'DM Mono',monospace", fontSize: 9, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8, marginTop: 16 },
  benchGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  benchUpdated: { fontFamily: "'DM Mono',monospace", fontSize: 9, color: 'var(--muted)', marginTop: 16 },
  benchEmpty: { fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, padding: '20px 0' },
  benchManageBtn: { marginTop: 12, width: '100%', padding: '8px', background: 'transparent', border: '1px solid var(--rule)', borderRadius: 4, fontSize: 11, color: 'var(--muted)', cursor: 'pointer', fontFamily: "'Work Sans',sans-serif", textAlign: 'center' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--rule)' },
  title: { fontFamily: "'News Cycle',serif", fontSize: 24, fontWeight: 700, color: 'var(--ink)' },
  statsRow: { display: 'flex', gap: 12 },
  statChip: { display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Mono',monospace", fontSize: 12, color: 'var(--ink)' },
  statDot: { width: 8, height: 8, borderRadius: '50%' },

  filterRow: { display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' },
  filterBtn: { padding: '6px 12px', background: 'transparent', border: '1px solid var(--rule)', borderRadius: 16, fontSize: 11, color: 'var(--muted)', cursor: 'pointer', fontFamily: "'Work Sans',sans-serif", fontWeight: 500 },
  filterActive: { background: 'var(--accent)', color: '#14110e', borderColor: 'var(--ink)' },

  empty: { textAlign: 'center', padding: '40px 20px' },
  emptyTitle: { fontFamily: "'News Cycle',serif", fontSize: 18, color: 'var(--ink)', marginBottom: 6 },
  emptyText: { fontSize: 13, color: 'var(--muted)' },

  list: { display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 },
  card: { background: 'var(--white)', border: '1px solid var(--rule)', borderRadius: 8, padding: '16px 20px' },
  cardTop: { display: 'flex', alignItems: 'flex-start', gap: 14 },
  scoreBox: { display: 'flex', alignItems: 'baseline', gap: 2, flexShrink: 0, minWidth: 40 },
  cardInfo: { flex: 1, minWidth: 0 },
  cardName: { fontSize: 14, fontWeight: 600, color: 'var(--ink)' },
  cardMeta: { fontFamily: "'DM Mono',monospace", fontSize: 11, color: 'var(--muted)', marginTop: 2 },
  tagRow: { display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' },
  siteTag: { padding: '2px 8px', borderRadius: 10, fontSize: 10, background: 'var(--cream)', border: '1px solid var(--rule)', color: 'var(--muted)', fontFamily: "'DM Mono',monospace" },
  stageBadge: { padding: '4px 10px', borderRadius: 12, fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono',monospace", letterSpacing: '0.3px', flexShrink: 0, textTransform: 'uppercase' },

  notesDisplay: { marginTop: 10, padding: '8px 12px', background: 'var(--cream)', borderRadius: 6, fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, cursor: 'pointer', borderLeft: '3px solid var(--rule)' },
  notesEdit: { marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 },
  notesInput: { padding: '8px 12px', borderRadius: 6, border: '1px solid var(--rule)', background: 'var(--cream)', fontSize: 12, fontFamily: "'Work Sans',sans-serif", color: 'var(--ink)', resize: 'vertical', outline: 'none' },
  notesSaveBtn: { padding: '6px 12px', background: 'var(--accent)', color: '#14110e', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: "'Work Sans',sans-serif" },
  notesCancelBtn: { padding: '6px 12px', background: 'transparent', color: 'var(--muted)', border: '1px solid var(--rule)', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: "'Work Sans',sans-serif" },

  cardActions: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--rule)' },
  actionsLeft: { display: 'flex', gap: 6 },
  actionsRight: { display: 'flex', gap: 6 },
  actionBtn: { padding: '5px 10px', background: 'transparent', border: '1px solid var(--rule)', borderRadius: 4, fontSize: 11, color: 'var(--muted)', cursor: 'pointer', fontFamily: "'Work Sans',sans-serif" },
  actionBtnPrimary: { padding: '5px 10px', background: 'var(--accent)', color: '#14110e', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: "'Work Sans',sans-serif" },
  stageBtn: { padding: '5px 10px', background: 'var(--cream)', border: '1px solid var(--rule)', borderRadius: 4, fontSize: 10, color: 'var(--ink)', cursor: 'pointer', fontFamily: "'DM Mono',monospace", fontWeight: 500 },
  deleteBtn: { padding: '5px 10px', background: 'transparent', border: 'none', fontSize: 10, color: 'var(--red)', cursor: 'pointer', fontFamily: "'DM Mono',monospace" },
  cardDate: { fontFamily: "'DM Mono',monospace", fontSize: 10, color: 'var(--muted)', marginTop: 8 },
};
